import { Combobox } from "@base-ui/react/combobox";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { Icon } from "../icon";
import {
  MenuItemLayout,
  menuItemClassName,
  menuSurfaceClassName,
} from "../menu/menu-internals";
import type { SelectOption, SelectProps } from "./select";

type SearchableSelectControlProps = Pick<
  SelectProps,
  | "id"
  | "options"
  | "disabled"
  | "readOnly"
  | "required"
  | "name"
  | "placeholder"
  | "label"
  | "data-testid"
  | "renderValue"
  | "renderOption"
  | "popupWidth"
> & {
  selectedOption: SelectOption | undefined;
  onValueChange: NonNullable<SelectProps["onChange"]>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerClassName: string;
};

export function SearchableSelectControl({
  id,
  options,
  selectedOption,
  onValueChange,
  open,
  onOpenChange,
  disabled,
  readOnly,
  required,
  name,
  placeholder,
  label,
  "data-testid": dataTestId,
  renderValue,
  renderOption,
  popupWidth,
  triggerClassName,
}: SearchableSelectControlProps) {
  const portalHost = usePortalHost();
  const [inputValue, setInputValue] = useState("");
  return (
    <Combobox.Root<SelectOption>
      value={selectedOption ?? null}
      onValueChange={(option, eventDetails) =>
        onValueChange(option?.value ?? null, eventDetails)
      }
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      name={name}
      modal={false}
      items={options}
      isItemEqualToValue={(option, selected) => option.value === selected.value}
      itemToStringLabel={(option) => option.label}
      itemToStringValue={(option) => option.value}
      open={open}
      inputValue={open ? inputValue : (selectedOption?.label ?? "")}
      onInputValueChange={setInputValue}
      onOpenChange={(open) => {
        onOpenChange(open);

        if (open) {
          setInputValue("");
        }
      }}
    >
      <Combobox.InputGroup
        className={cn(
          triggerClassName,
          "relative",
          "[&:not([data-readonly])]:focus-within:border-input-active-outline",
          disabled && "cursor-not-allowed",
        )}
      >
        {!open && (
          <span
            className={cn(
              "pointer-events-none absolute inset-y-0 left-(--spacing-10) flex min-w-0 items-center text-left text-(length:--font-size-body-medium) leading-(--font-line-height-20)",
              readOnly ? "right-(--spacing-10)" : "right-(--spacing-32)",
            )}
          >
            {renderValue ? (
              renderValue(selectedOption)
            ) : selectedOption ? (
              <span className="truncate">{selectedOption.label}</span>
            ) : !readOnly ? (
              <span className="truncate text-input-text-placeholder">
                {placeholder}
              </span>
            ) : null}
          </span>
        )}
        <Combobox.Input
          id={id}
          data-testid={dataTestId}
          aria-label={
            label || id
              ? undefined
              : readOnly
                ? "Select option"
                : (placeholder ?? "Select option")
          }
          aria-required={required || undefined}
          tabIndex={readOnly ? -1 : undefined}
          className={cn(
            "min-w-0 flex-1 bg-transparent outline-none text-left text-(length:--font-size-body-medium) leading-(--font-line-height-20) text-input-text-default placeholder:text-input-text-placeholder",
            !open && "text-transparent caret-transparent",
          )}
          placeholder={open ? "Search" : undefined}
          onFocus={(event) => event.currentTarget.select()}
          onMouseDown={(event) => {
            if (readOnly) event.preventDefault();
          }}
        />
        {!readOnly && (
          <Combobox.Trigger
            aria-label="Open options"
            className="inline-flex shrink-0 text-input-text-default outline-none"
          >
            <Icon name="chevron-down" size="xs" tone="inherit" />
          </Combobox.Trigger>
        )}
      </Combobox.InputGroup>

      <Combobox.Portal container={portalHost}>
        {/* Collision detection must measure the menu's minimum width, not only the trigger. */}
        <Combobox.Positioner
          sideOffset={4}
          className={cn(
            "z-[var(--z-layer-popover)]",
            popupWidth === "content"
              ? "w-auto min-w-(--anchor-width) max-w-[calc(100vw-var(--spacing-16))]"
              : "w-(--anchor-width) min-w-[180px] max-w-[calc(100vw-var(--spacing-16))]",
          )}
        >
          <Combobox.Popup className={menuSurfaceClassName({ size: "auto" })}>
            <Combobox.List className="flex flex-col outline-none">
              {(option: SelectOption) => (
                <Combobox.Item
                  key={option.value}
                  value={option}
                  disabled={option.disabled}
                  className={menuItemClassName({})}
                >
                  {option.icon && (
                    <span className="inline-flex shrink-0 text-menu-icon-default">
                      <Icon name={option.icon} size="xs" tone="inherit" />
                    </span>
                  )}
                  <MenuItemLayout
                    checked={option.value === selectedOption?.value}
                  >
                    {renderOption ? renderOption(option) : option.label}
                  </MenuItemLayout>
                </Combobox.Item>
              )}
            </Combobox.List>
            <Combobox.Empty className="[&:empty]:p-0 px-(--spacing-10) py-(--spacing-8) text-(length:--font-size-body-medium) leading-(--font-line-height-20) text-menu-text-subtle">
              No results available
            </Combobox.Empty>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}
